"use client";

import { useCallback, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useAlert } from "@/hooks/useAlert";

export type NamespacedAlertStatus = "success" | "error" | "info";

export type NamespacedAlert = (
  status: NamespacedAlertStatus,
  key: string,
  params?: Record<string, unknown>
) => void;

const ERROR_SECONDS_TO_CLOSE = 10;
const DEFAULT_SECONDS_TO_CLOSE = 4;

/**
 * Toasts for one integration's key block: `raise("error", "loadError")` becomes
 * `t("metaIntegration.loadError")`.
 *
 * `setAlert` and `t` are read through a ref so the returned callback is stable
 * for the life of the hook — depending on them directly would re-run the
 * callers' load effects on every render.
 */
export function useNamespacedAlert(
  keyPrefix: string,
  namespace = "partners"
): NamespacedAlert {
  const { t } = useTranslation(namespace);
  const { setAlert } = useAlert();

  const alertRef = useRef({ setAlert, t, keyPrefix });
  useEffect(() => {
    alertRef.current = { setAlert, t, keyPrefix };
  }, [setAlert, t, keyPrefix]);

  return useCallback((status, key, params) => {
    const { setAlert: raise, t: translate, keyPrefix: prefix } = alertRef.current;
    raise({
      status,
      message: translate(`${prefix}.${key}`, params),
      secondsToClose:
        status === "error" ? ERROR_SECONDS_TO_CLOSE : DEFAULT_SECONDS_TO_CLOSE,
    });
  }, []);
}
